"use client";

import { useMemo, useState } from "react";
import type { JobPosting } from "@/lib/types";

/**
 * Open-jobs table matching https://big.dk/careers:
 * Location/Department native selects, 4-col header (Title spans 2),
 * uppercase rows linking to external ATS with hover:#f3f3f3.
 */
export function JobsList({
  heading,
  jobs,
}: {
  heading: string;
  jobs: JobPosting[];
}) {
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");

  const locations = useMemo(
    () => [...new Set(jobs.map((j) => j.location).filter(Boolean))].sort(),
    [jobs],
  );
  const departments = useMemo(
    () => [...new Set(jobs.map((j) => j.department).filter(Boolean))].sort(),
    [jobs],
  );

  const filtered = useMemo(
    () =>
      jobs.filter(
        (j) =>
          (!location || j.location === location) &&
          (!department || j.department === department),
      ),
    [jobs, location, department],
  );

  return (
    <div className="mt-10 px-7 md:pl-17.5 lg:pl-25 xl:pl-32.5">
      <h2 className="mb-8 text-[42px] leading-[1.14em] uppercase">{heading}</h2>

      <div className="mb-8 flex flex-col gap-3 text-sm lg:flex-row lg:gap-5">
        <label className="flex items-center">
          <span className="shrink-0">Location</span>
          <select
            className="text-big-gray ml-3 max-lg:w-full border-0 bg-transparent outline-none"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          >
            <option value="">All</option>
            {locations.map((loc) => (
              <option key={loc} value={loc}>
                {loc}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center">
          <span className="shrink-0">Department</span>
          <select
            className="text-big-gray ml-3 max-lg:w-full border-0 bg-transparent outline-none"
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
          >
            <option value="">All</option>
            {departments.map((dep) => (
              <option key={dep} value={dep}>
                {dep}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-4 border-b border-[#c4c4c4] pb-4 text-base uppercase">
        <div className="col-start-1 col-end-3 hidden lg:block">Title</div>
        <div className="col-start-3 col-end-4 hidden lg:block">Department</div>
        <div className="col-start-4 col-end-5 hidden lg:block">Location</div>
      </div>

      {filtered.map((job) => {
        const rowClass =
          "grid grid-cols-1 border-b border-[#c4c4c4] py-3 text-xs uppercase transition-colors hover:bg-[#f3f3f3] lg:grid-cols-4";
        const inner = (
          <>
            <div className="lg:col-start-1 lg:col-end-3">
              <span className="inline-block w-20 text-[10px] text-[#898989] lg:hidden">
                Title
              </span>
              {job.title}
            </div>
            <div className="lg:col-start-3 lg:col-end-4">
              <span className="inline-block w-20 text-[10px] text-[#898989] lg:hidden">
                Department
              </span>
              {job.department}
            </div>
            <div className="lg:col-start-4 lg:col-end-5">
              <span className="inline-block w-20 text-[10px] text-[#898989] lg:hidden">
                Location
              </span>
              {job.location}
            </div>
          </>
        );

        if (job.applyUrl) {
          return (
            <a
              key={job.id}
              href={job.applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={rowClass}
            >
              {inner}
            </a>
          );
        }

        return (
          <div key={job.id} className={rowClass}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}
